import React, { useState } from 'react';
import EditorialArticle from '../../components/redesign/EditorialArticle.jsx';
import SEO from '../../components/SEO.jsx';
import { trackCTAClick } from '../../utils/analytics.js';

const CALENDLY_URL = 'https://calendly.com/aakeeo-technologies/crm-consultations';
const EMAIL = 'sales@aakeeo.com';
const PHONE = '+91-8431748754';
const WHATSAPP = 'https://wa.me/918431748754';
const LINKEDIN = 'https://www.linkedin.com/company/aakeeo-technologies-pvt-ltd';
const X_URL = 'https://x.com/AakeeoTech';

export function ContactA({ onNav }) {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', query: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validateForm = () =>
    formData.name.trim() && formData.email.trim() && formData.phone.trim() && formData.query.trim();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);
    setSubmitStatus(null);
    try {
      const params = new URLSearchParams();
      Object.entries(formData).forEach(([k, v]) => params.append(k, v));
      const url = `https://n8n.aakeeocrm.com/webhook/5fed7f08-2230-4d07-a78b-7db4c26c0246?${params.toString()}`;
      // `no-cors` makes the response opaque (we can't read status) but
      // guarantees the request fires without CORS preflight failures.
      // For this n8n endpoint we treat "request dispatched" as success —
      // a real failure would throw on the fetch itself.
      await fetch(url, { method: 'GET', mode: 'no-cors' });
      trackCTAClick('contact_form_submit', 'n8n_webhook');
      setSubmitStatus('success');
      setFormData({ name: '', email: '', phone: '', query: '' });
    } catch (err) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitStatus(null), 6000);
    }
  };

  return (
    <EditorialArticle
      navPage="contact"
      onNav={onNav}
      eyebrow="Get in touch"
      title={<>Book your free 30-minute <em>CRM audit</em>.</>}
      lead="Tell us about your process. We'll run the diagnostics and tell you — in plain language — whether a system re-think is the right call."
    >
      <SEO
        title="Contact Us | Aakeeo Technologies"
        description="Book a free 30-minute CRM consultation with Aakeeo. Talk to an expert about SuiteCRM, CRM migration, or AI automation. Based in Bangalore, India."
        keywords={['contact Aakeeo', 'RevOps consultation', 'business automation', 'SuiteCRM expert']}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: 'Aakeeo Technologies',
          url: 'https://www.aakeeo.com',
          logo: 'https://www.aakeeo.com/Aakeeo_logo.png',
          contactPoint: {
            '@type': 'ContactPoint',
            telephone: '+91-8431748754',
            contactType: 'sales',
            email: EMAIL,
            availableLanguage: 'English',
          },
        }}
      />

      <section className="contactA-grid">
        <div className="contactA-channels">
          <span className="eyebrowA">Direct channels</span>
          <h2>Pick whichever feels right.</h2>

          <ul className="contactA-list">
            <li>
              <div className="contactA-tag">Calendar</div>
              <h3>Schedule a diagnostic</h3>
              <p>A 30-minute working call with someone who&apos;ll actually do the build — not a salesperson.</p>
              <a className="contactA-link" href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">Open Calendly →</a>
            </li>
            <li>
              <div className="contactA-tag">Email</div>
              <h3>Send an RFP or detailed brief</h3>
              <p>We reply within one business day, from India (IST). Faster for well-scoped briefs.</p>
              <a className="contactA-link" href={`mailto:${EMAIL}`}>{EMAIL}</a>
            </li>
            <li>
              <div className="contactA-tag">Phone / WhatsApp</div>
              <h3>Talk directly</h3>
              <p>For urgent scoping, existing client support, or anything that reads better out loud than written.</p>
              <div className="contactA-linkRow">
                <a className="contactA-link" href={WHATSAPP} target="_blank" rel="noopener noreferrer">WhatsApp</a>
                <span aria-hidden>·</span>
                <a className="contactA-link" href={`tel:${PHONE.replace(/[^+0-9]/g, '')}`}>{PHONE}</a>
              </div>
            </li>
            <li>
              <div className="contactA-tag">Social</div>
              <h3>Follow the work</h3>
              <p>Case studies, open-source updates, and the occasional rant about per-seat pricing.</p>
              <div className="contactA-linkRow">
                <a className="contactA-link" href={LINKEDIN} target="_blank" rel="noopener noreferrer">LinkedIn</a>
                <span aria-hidden>·</span>
                <a className="contactA-link" href={X_URL} target="_blank" rel="noopener noreferrer">X</a>
              </div>
            </li>
          </ul>
        </div>

        <div className="contactA-formWrap">
          <form className="contactA-form" onSubmit={handleSubmit}>
            <span className="eyebrowA">Direct message</span>
            <h2>Send us a brief.</h2>
            <p className="contactA-formLead">Share your current setup, goals, and timeline. We respond to every message a real person writes.</p>

            <label className="contactA-field">
              <span>Full name</span>
              <input type="text" name="name" required placeholder="Jane Doe" value={formData.name} onChange={handleInputChange} />
            </label>

            <div className="contactA-row">
              <label className="contactA-field">
                <span>Work email</span>
                <input type="email" name="email" required placeholder="jane@company.com" value={formData.email} onChange={handleInputChange} />
              </label>
              <label className="contactA-field">
                <span>Phone</span>
                <input type="tel" name="phone" required placeholder="+1 555 000 0000" value={formData.phone} onChange={handleInputChange} />
              </label>
            </div>

            <label className="contactA-field">
              <span>What are you trying to solve?</span>
              <textarea
                name="query"
                rows="5"
                required
                placeholder="We're currently using… and the problem is…"
                value={formData.query}
                onChange={handleInputChange}
              />
            </label>

            <button
              type="submit"
              className="btnA-primary"
              disabled={!validateForm() || isSubmitting}
              style={{ opacity: !validateForm() || isSubmitting ? 0.6 : 1 }}
            >
              {isSubmitting ? 'Sending…' : 'Send brief'}
            </button>

            {submitStatus === 'success' && (
              <p className="contactA-note contactA-note--ok">Thanks — we&apos;ll be in touch within one business day.</p>
            )}
            {submitStatus === 'error' && (
              <p className="contactA-note contactA-note--err">Something went wrong. Please email {EMAIL} instead.</p>
            )}
          </form>
        </div>
      </section>
    </EditorialArticle>
  );
}
